// src/lib/services/orderService.js
import { supabase } from "@/lib/supabaseClient";

/**
 *  USER: Fetch all orders of logged-in user
 */
export async function fetchUserOrders(userId) {
  if (!userId) throw new Error("User not logged in");

  const { data: buyerRow, error: buyerErr } = await supabase
    .from("buyers")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  if (buyerErr) throw new Error(buyerErr.message);
  if (!buyerRow?.id) return [];

  const { data, error } = await supabase
    .from("orders")
    .select(
      `
        id,
        order_id,
        status,
        payment_status,
        return_status,
        subtotal,
        tax,
        shipping,
        total,
        created_at,
        updated_at,
        shipping_address,
        billing_address,
        payment_method,
        tracking_number,
        lsp_name,
        notes,
        order_items (
          id,
          quantity,
          price,
          product_id,
          product_name,
          product_image,
          subtotal,
          handling_time,
          delivery_days,
          delivery_date_iso,
          delivery_text
        )
      `
    )
    .eq("buyer_id", buyerRow.id)
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);

  return data || [];
}

/**
 *  USER: Fetch single order by UUID (orders.id)
 */
export async function fetchUserOrderById(userId, orderUuid) {
  if (!userId) throw new Error("User not logged in");
  if (!orderUuid) throw new Error("Order ID missing");

  const { data: buyerRow, error: buyerErr } = await supabase
    .from("buyers")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  if (buyerErr) throw new Error(buyerErr.message);
  if (!buyerRow?.id) return null;

  const { data, error } = await supabase
    .from("orders")
    .select(
      `
        id,
        order_id,
        status,
        payment_status, 
        return_status,
        subtotal,
        tax,
        shipping,
        total,
        created_at,
        updated_at,
        shipping_address,
        billing_address,
        payment_method,
        tracking_number,
        lsp_name,
        notes,
        order_items (
          id,
          quantity,
          price,
          product_id,
          product_name,
          product_image,
          subtotal,
          handling_time,
          delivery_days,
          delivery_date_iso,
          delivery_text
        )
      `
    )
    .eq("id", orderUuid)
    .eq("buyer_id", buyerRow.id)
    .maybeSingle();

  if (error) throw new Error(error.message);

  return data || null;
}

/**
 *  USER: Create Order
 */
export async function createOrder(payload, dbClient = supabase) {
  const {
    userId,
    items = [],
    shippingAddress,
    billingAddress,
    paymentMethod = "paypal",
    paymentStatus = "pending",        //ADD THIS
    paypalTransactionId = null,
    subtotal = 0,
    tax = 0,
    shipping = 0,
    total = 0,
    notes = null,
  } = payload || {};

  if (!userId) throw new Error("User not logged in");
  if (!items?.length) throw new Error("Cart is empty");

  let buyerId = null;

  const { data: buyerRow, error: buyerFetchErr } = await dbClient
    .from("buyers")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  if (buyerFetchErr) throw new Error(buyerFetchErr.message);

  if (buyerRow?.id) {
    buyerId = buyerRow.id;
  } else {
    const { data: buyerCreated, error: buyerCreateErr } = await dbClient
      .from("buyers")
      .insert([
        {
          user_id: userId,
          email: shippingAddress?.email || null,
          full_name: shippingAddress?.name || null,
          phone: shippingAddress?.phone || null,
        },
      ])
      .select("id")
      .single();

    if (buyerCreateErr) throw new Error(buyerCreateErr.message);

    buyerId = buyerCreated?.id;
  }

  if (!buyerId) throw new Error("Buyer not found/created");

  const orderInsertPayload = {
    user_id: userId,
    buyer_id: buyerId,
    status: "pending",
    subtotal: Number(subtotal || 0),
    tax: Number(tax || 0),
    shipping: Number(shipping || 0),
    total: Number(total || 0),
    payment_method: paymentMethod,
    payment_status: paymentStatus,            //ADD THIS
    paypal_transaction_id: paypalTransactionId,  //ADD THIS
    shipping_address: shippingAddress || null,
    billing_address: billingAddress || null,
    notes: notes || null,
  };

  const { data: orderRow, error: orderErr } = await dbClient
    .from("orders")
    .insert([orderInsertPayload])
    .select("id, order_id, buyer_id, user_id")
    .single();

  if (orderErr) throw new Error(orderErr.message);

  const orderUuid = orderRow?.id;
  if (!orderUuid) throw new Error("Order not created");

  const orderItemsPayload = (items || []).map((it) => ({
    order_id: orderUuid,
    product_id: it?.productId || it?.id || null,
    product_name: it?.name || it?.product_name || "Product",
    product_image: it?.image || it?.product_image || "",
    quantity: Number(it?.quantity || 1),
    price: Number(it?.price || 0),
    subtotal: Number(it?.price || 0) * Number(it?.quantity || 1),

    handling_time: Number(it?.handlingTime || it?.handling_time || 1),
    delivery_days: Number(it?.deliveryDays || it?.delivery_days || 10),
    delivery_date_iso: it?.deliveryDateISO || it?.delivery_date_iso || null,
    delivery_text: it?.deliveryText || it?.delivery_text || null,
  }));

  const { error: itemsErr } = await dbClient
    .from("order_items")
    .insert(orderItemsPayload);

  if (itemsErr) throw new Error(itemsErr.message);

  return {
    success: true,
    orderId: orderUuid,
    orderCode: orderRow?.order_id,
    buyerId: orderRow?.buyer_id,
    userId: orderRow?.user_id,
  };
}

/**
 *  ADMIN: Order Analytics fetcher
 */
export async function getOrderAnalytics({ dateFrom, dateTo }) {
  if (!dateFrom || !dateTo) throw new Error("dateFrom and dateTo required");

  const res = await fetch(
    `/api/admin/orders/analytics?from=${dateFrom}&to=${dateTo}`
  );

  const json = await res.json();

  if (!json?.success) {
    throw new Error(json?.error || "Failed to load analytics");
  }

  return json?.analytics;
}